import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useRegion } from "../context/RegionContext";

export function Wordmark({ size = "sm" }) {
  const { region } = useRegion();
  const words = region.brand_title.split(" ");
  if (size === "sm")
    return (
      <div className="font-serif uppercase leading-[0.9] text-navy" data-testid="wordmark-small">
        {words.map((w) => <div key={w} className="text-[15px] tracking-wide">{w}</div>)}
      </div>
    );
  return (
    <div className="text-center" data-testid="wordmark-large">
      <h1 className="relative inline-block whitespace-nowrap border-b-2 border-navy pb-1 font-serif uppercase leading-none text-navy text-[clamp(1.75rem,10vw,5.5rem)]">
        {region.brand_title}
      </h1>
    </div>
  );
}

export function LangTextToggle({ className = "" }) {
  const { lang, setLang, languages } = useLang();
  if (languages.length < 2) return null;
  return (
    <div className={`inline-flex items-center rounded-full bg-white/75 px-2.5 py-1 text-[12px] shadow-sm backdrop-blur-md ${className}`} data-testid="lang-toggle">
      {languages.map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="px-1 text-slate-300">|</span>}
          <button
            onClick={() => setLang(l)}
            aria-pressed={lang === l}
            data-testid={`lang-${l}`}
            className={`uppercase transition-colors duration-200 ${lang === l ? "font-extrabold text-[#0D47A1]" : "font-semibold text-slate-400 hover:text-navy"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
}

export function LangToggle({ className = "" }) {
  const { lang, setLang, languages } = useLang();
  if (languages.length < 2) return null;
  return (
    <div className={`inline-flex rounded-full bg-white/85 p-0.5 text-xs font-bold shadow-sm backdrop-blur ${className}`} data-testid="lang-toggle">
      {languages.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-200 ${
            lang === l ? "bg-brand text-white" : "text-navy hover:bg-mist"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, sub, action, testid = "empty-state" }) {
  return (
    <div className="mx-auto flex max-w-sm flex-col items-center px-6 py-14 text-center fade-up" data-testid={testid}>
      <div className="relative mb-5">
        <div className="absolute inset-0 -z-0 scale-150 rounded-full bg-brand/10 blur-xl" />
        <div className="relative flex h-20 w-20 items-center justify-center rounded-[28px] bg-white shadow-[0_8px_30px_rgba(13,42,107,0.12)]">
          <Icon className="h-9 w-9 text-brand" strokeWidth={1.6} />
        </div>
      </div>
      <h3 className="text-lg font-extrabold text-navy">{title}</h3>
      {sub && <p className="mt-2 text-sm leading-relaxed text-slate-500">{sub}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function SubPage({ title, children, testid, right }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-[100dvh] bg-mist pb-safe" data-testid={testid}>
      <header className="sticky top-0 z-20 border-b border-slate-100 bg-white/90 backdrop-blur-md pt-safe">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/perfil"))}
            className="-ml-2 rounded-full p-2 text-navy transition-colors hover:bg-mist"
            data-testid="subpage-back-btn"
            aria-label="back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="flex-1 truncate text-lg font-extrabold text-navy">{title}</h1>
          {right}
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-5">{children}</main>
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <div className={`h-6 w-6 animate-spin rounded-full border-2 border-brand/25 border-t-brand ${className}`} />;
}
